import { APIDashboardAttributes } from './dashboardAPITypes';
import {
  APIComponentAttributes,
  APIModelAttributes,
  APIReferenceAttributes,
  APIScenarioAttributes,
  APITagAttributes,
  AssetType,
  DirectedTriple,
  Named,
  PathResult,
  PersistedMetamodel,
  WithArdoqPersistent,
} from './apiTypes';
import { ArdoqId, Entity, MetaData, Versioned } from './coreTypes';
import { APIDiffContext } from './DiffAPITypes';
import { APIWorkspaceAttributes } from './workspaceAPITypes';
import {
  APIScopeData,
  APIScopeDataWithExplicitParentChildReferences,
} from './scopeDataAPITypes';
import { APISlideAttributes } from './slideApiTypes';
import { PathCollapsingRule } from './traversal';
import { APICurrentUserConfidentialFieldPermissions } from './confidentialField';

export type APIPresentationAssetAttributes = Entity &
  Named &
  Versioned &
  MetaData &
  WithArdoqPersistent & {
    ardoq: { 'entity-type': AssetType.PRESENTATION };
    description?: string;
    label: string;
    lockedContext: boolean;
    readAccess: PresentationReadPermissions;
    writeAccess: PresentationWritePermissions;
    slides: ArdoqId[];
    authorizedSlides?: ArdoqId[];
  };

export enum PresentationReadPermissions {
  ME = 'me',
  WS = 'ws',
  CONTRIBUTOR = 'contributor',
  ALL = 'all',
}

export enum PresentationWritePermissions {
  ME = 'me',
  WS = 'ws',
}

export type LoadedStateArgs = {
  paths: DirectedTriple[][];
  startSetResult: ArdoqId[];
  pathCollapsingRules: PathCollapsingRule[];
  pathResults?: PathResult[];
  // We will expose the scope data for each loaded state to handle path
  // collapsing properly for presentation slides. Till that transition phase
  // has been completed, this field will be optional.
  scopeData?: APIScopeDataWithExplicitParentChildReferences;
};

export type APIPresentationAttributes = Omit<
  APIPresentationAssetAttributes,
  'authorizedSlides'
> & {
  authorizedSlides: APISlideAttributes[];
  components: APIComponentAttributes[];
  dashboards: APIDashboardAttributes[];
  dynamicFilterResults: any[];
  fields: any[];
  fieldAccessContext: APICurrentUserConfidentialFieldPermissions;
  metamodels: Record<string, PersistedMetamodel>;
  models: APIModelAttributes[];
  plugins: any[];
  references: APIReferenceAttributes[];
  scenarios: APIPresentationScenarioAttributes[];
  tags: APITagAttributes[];
  workspaces: APIWorkspaceAttributes[];
  traversalExecute?: Record<
    string,
    {
      loadedStates: LoadedStateArgs[];
      // This will be removed. Till that is done, this field will be optional.
      scopeData?: APIScopeDataWithExplicitParentChildReferences;
    }
  >;
};

export type EnhancedPresentation = APIPresentationAttributes & {
  componentsById: Partial<Record<ArdoqId, APIComponentAttributes>>;
};

export type APIPresentationScenarioAttributes = APIScenarioAttributes & {
  scope: APIScopeData;
  diffContext: APIDiffContext;
};
